import { useTheme } from "@mui/material/styles";
import Step from "@mui/material/Step";
import Stepper from "@mui/material/Stepper";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";

import strings from "core/strings";

import Box from "ui/atom/Box";
import Form from "ui/atom/Form";
import Alert from "ui/base/Alert";
import Button from "ui/base/Button";
import Input from "ui/base/Input";
import Message from "ui/Message";

import { usePartnerSignup } from "areas/partners/fabric";


const Copy = ({ validated }) => {
	if (!validated) return (
		<Box.Column gap={2}>
			<Typography>
				Please look for an email from <b>Subly@Subly.ink</b> to your company email account, which contains a link to validate your company email address. If you can’t find it, please check your Junk folder(s) just in case.
			</Typography>
			<Typography>
				Once you click on that link to validate your company email, we will create your media partner account and let you know when it’s ready for you to complete the simple integration process.
			</Typography>
		</Box.Column>
	);

	return (
		<Box.Column gap={1}>
			<Typography>We’ll review your registration within two (2) business days. We do this to ensure the integrity of our media partner companies and publications.</Typography>
			<Typography>Once reviewed and approved, you will receive a sign-in URL with details on how to add Subly to your publication.</Typography>
			<Typography>It’s our goal to get you running within an hour. If your CMS or paywall provider doesn’t support Subly yet, we’ll work together to get your provider supporting Subly ASAP.</Typography>
			<Typography>Thank you!</Typography>
		</Box.Column>
	);
};

SignupForm.Review = () => {
	const partner = usePartnerSignup();
	const theme = useTheme();

	const style = {
		stepper: {
			my: theme.spacing(2),
			".MuiSvgIcon-root:is(.Mui-completed)": {
				color: theme.palette.success.main
			},
			".MuiSvgIcon-root:is(.Mui-active)": {
				color: theme.palette.secondary.main
			}
		}
	};

	return (
		<Box.Column gap={2}>
			<Input value={partner.query.data.name} label="Your publication name" disabled />
			<Input value={partner.query.data.url} label="Your publication URL" disabled />
			<Stepper activeStep={partner.current} sx={style.stepper}>
				<Step completed>
					<StepLabel>Fill form</StepLabel>
				</Step>
				<Step completed={partner.step.validate}>
					<StepLabel>Validate Account</StepLabel>
				</Step>
				<Step completed={partner.step.approved}>
					<StepLabel>Review Application</StepLabel>
				</Step>
			</Stepper>
			<Copy validated={partner.step.validate}/>
			<Button.Link to="/" variant="contained" color="secondary">Go to Home</Button.Link>
		</Box.Column>
	);
};

export default function SignupForm() {
	const partner = usePartnerSignup();

	const style = {
		actions: {
			mt: 2
		}
	};

	const submit = async (e) => {
		e.preventDefault();
		const formData = new FormData(e.target);
		const data = Object.fromEntries(formData.entries());

		const body = {
			contact: {
				name: data.contactName.trim(),
				title: data.contactTitle.trim(),
				email: data.contactEmail.trim(),
				phone: data.contactPhone ? strings.sanitizePhoneNumber("+1" + data.contactPhone.trim()) : null, /* todo: improve country code */
				company: data.contactCompany.trim()
			},
			partner: {
				name: data.publicationName.trim(),
				url: strings.trimTrailingSlash(data.publicationUrl.trim())
			}
		};

		await partner.mutateAsync(body);
		e.target.reset();
	};

	if (partner.query.data) return <SignupForm.Review />;
	else if (partner.isError) return <Message icon="triangle-alert" color="error" text="Well this is unexpected!" back={partner.reset} />;
	return (
		<Form onSubmit={submit}>
			{partner.disabled && (
				<Alert variant="filled" severity="error">
					Thank you for your interest! Please validate and use your personal Subly account before registering as a media partner
				</Alert>
			)}
			<Input name="contactName" label="Full Name" placeholder="Enter your full name" required />
			<Input name="contactTitle" label="Title" placeholder="What is your role at the company? (ex. Circulation Manager)" required />
			<Input name="contactEmail" label="Company Email" type="email" placeholder="Enter your company email address" required />
			<Input name="contactPhone" label="Company Phone" type="tel" placeholder="Enter your company phone number" />
			<Input name="contactCompany" label="Company Name" placeholder="Enter your company’s name (ex. Times Publishing Co.)" required />

			<Input name="publicationName" label="Publication Name" placeholder="Enter your publication name (ex. The Bellevue Tribune)" required />
			<Input name="publicationUrl" label="Publication URL" type="url" placeholder="Enter your publication URL (ex. mypaper.com)" required />

			<Box.Column gap={1} sx={style.actions}>
				<Button type="submit" variant="contained" color="secondary" loading={partner.isPending} disabled={partner.disabled || partner.isPending}>Submit</Button>
				<Button.Link to="/partners" disabled={partner.isPending}>Cancel</Button.Link>
			</Box.Column>
		</Form>
	);
};